import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const CURRENCIES = ['USD', 'EUR', 'KMF', 'KZT'];

export default function AdminRatesPage() {
  const [rates, setRates] = useState({});
  const [fee, setFee] = useState(1.5);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get('/admin/rates').then(r => { setRates(r.data.rates || {}); setFee(r.data.fee_percent); });
  useEffect(() => { load(); }, []);

  const setPair = (pair, value) => setRates(rr => ({ ...rr, [pair]: value }));

  const save = async () => {
    setBusy(true);
    const list = [];
    for (const [pair, r] of Object.entries(rates)) {
      const n = Number(r);
      if (Number.isFinite(n) && n > 0) list.push({ pair, rate: n });
    }
    try {
      await adminApi.post('/admin/rates', { rates: list, fee_percent: Number(fee) });
      toast.success('Rates updated');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Rates & fees</h1>
        <Button onClick={save} disabled={busy} data-testid="admin-rates-save-button" className="bg-[hsl(var(--brand-navy))]"><Save className="h-4 w-4 mr-2" />Save</Button>
      </div>
      <Card className="bg-white"><CardContent className="p-6 max-w-sm">
        <Label>Exchange fee (%)</Label>
        <Input type="number" step="0.01" min="0" max="100" value={fee} onChange={e => setFee(e.target.value)} className="tabular-nums" data-testid="admin-fee-input" />
      </CardContent></Card>
      <Card className="bg-white" data-testid="admin-rates-table">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-3 text-left">From \ To</th>{CURRENCIES.map(c => <th key={c} className="p-3 text-center">{c}</th>)}</tr></thead>
            <tbody>
              {CURRENCIES.map(fromC => (
                <tr key={fromC} className="border-t">
                  <td className="p-3 font-medium bg-[hsl(var(--secondary))]/40">{fromC}</td>
                  {CURRENCIES.map(toC => {
                    if (fromC === toC) return <td key={toC} className="p-3 text-center text-[hsl(var(--muted-foreground))]">—</td>;
                    const pair = `${fromC}_${toC}`;
                    return (
                      <td key={toC} className="p-2">
                        <Input type="number" step="0.000001" value={rates[pair] ?? ''} onChange={e => setPair(pair, e.target.value)} className="h-8 tabular-nums" data-testid={`admin-rate-${pair}`} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
